import { ReactElement } from "react"
import { faCloudDownload } from "@fortawesome/free-solid-svg-icons"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { Blockquote } from "@wfp/ui"
import useCommon from "hooks/useCommon"
import RenderRow from "components/Shared/Property/RenderRow"
import {
  useDownloadJsonSchemasByIdMutation,
  useDownloadXlsFormSchemasByIdMutation,
} from "services/household/xlsFormSchemas"
import { XLSFormSchemaByID } from "interfaces/dataInterfaces"

type Props = {
  data: XLSFormSchemaByID
}

const XlsFormData = ({ data }: Props): ReactElement => {
  const { showSnackBar } = useCommon()
  const [downloadJSON, { isLoading: loadingJSON }] = useDownloadJsonSchemasByIdMutation()
  const [downloadFile, { isLoading: loadingFile }] = useDownloadXlsFormSchemasByIdMutation()

  const handleDownloadCSV = async (fileId: number, sourceName?: string): Promise<void> => {
    try {
      await downloadFile({ fileId, sourceName }).unwrap()
    } catch (error) {
      showSnackBar("An error occurred while downloading a required file", "error")
    }
  }

  const handleDownloadJSON = async (id: number): Promise<void> => {
    try {
      await downloadJSON({ id, module: "household" }).unwrap()
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar(
        (error?.data as { message: string })?.message ?? "An error occurred while downloading a required file",
        "error",
      )
    }
  }

  return (
    <div className="p-3">
      {data?.xlsFormUpdateDate && (
        <Blockquote kind="warning" withIcon className="mb-2">
          <p className="py-1">The original XLSForm file is no longer updated.</p>
        </Blockquote>
      )}

      <table>
        <RenderRow
          label="Original XLSX file"
          value={data?.xlsFormSourceFile}
          icon={faCloudDownload}
          action={() => {
            data && handleDownloadCSV(data.xlsFormId, data?.xlsFormSourceFile)
          }}
          isLoading={loadingFile}
        />
        <RenderRow
          label="Current form definition JSON"
          value="View"
          icon={faCloudDownload}
          action={() => {
            data && handleDownloadJSON(data.xlsFormId)
          }}
          isLoading={loadingJSON}
        />
      </table>
    </div>
  )
}

export default XlsFormData
